import { ECONOMY } from '@/config/economy';
import { GAME } from '@/config/game';
import { PLAYER } from '@/config/player';
import type { InputDevice, InputState } from '@/input/types';
import type { DeathFlow } from '@/run/death-flow';
import type { Hangar } from '@/run/hangar';
import type { Rewards } from '@/run/rewards';
import type { RunState } from '@/run/run-state';
import type { ScreenSize, ShopFlow } from '@/run/shop-flow';
import type { WaveDirector } from '@/run/wave-director';
import type { World } from '@/scene/world';
import type { Hud } from '@/ui/hud';
import type { RecordToasts } from '@/ui/record-toasts';
import { sfx } from '@/audio/sfx';
import type { Collisions } from './collisions';
import { applyAutoAim, updateHoming } from './targeting';

/** Everything one fixed fight step drives (game.ts wires it once). */
export interface FightDeps {
  readonly world: World;
  readonly run: RunState;
  readonly waves: WaveDirector;
  readonly collisions: Collisions;
  readonly rewards: Rewards;
  readonly death: DeathFlow;
  readonly shop: ShopFlow;
  readonly hangar: Hangar;
  readonly toasts: RecordToasts;
  readonly hud: Hud;
  readonly view: ScreenSize;
}

/**
 * One 60 Hz step of the arena (no overlay open): ship, bullets, pickups,
 * waves or the boss, hit routing, hazards and the portal exit.
 */
export class FightStep {
  constructor(private readonly d: FightDeps) {}

  step(dt: number, input: InputState, device: InputDevice): void {
    const { world, run, waves, collisions, rewards, hud } = this.d;
    const { player, bullets } = world;
    const held = run.wave.bossHeld;
    run.stats.timeAlive += dt;
    applyAutoAim(input, device, world, held);

    const boss = world.boss;
    updateHoming(world, held);
    // Chronos time-warp zones scale the ship's movement (B15)
    player.envSpeedMul =
      boss && !held && !boss.dead ? boss.playerSpeedMul(player.x, player.y) : 1;
    player.update(dt, input, bullets, world.arena, run.rng);
    bullets.update(dt, world.arena);
    world.drops.update(dt);
    hud.update(dt);

    const magnetRadius = ECONOMY.gems.magnetRadius * player.stats.magnetMul;
    const collected = world.gems.update(dt, player.x, player.y, magnetRadius);
    rewards.collectGems(collected.yellow, collected.red);

    if (boss && !boss.dead) {
      if (run.wave.bossHeld) {
        // Wave phase (docs/12 C8): the boss is offstage, minions carry it
        world.minions.update(dt, boss.x, boss.y, player.x, player.y, bullets);
        collisions.wavePhase();
        waves.tickHeld(dt);
      } else {
        boss.update(dt, player.x, player.y, bullets, world.telegraphs);
        waves.tickAdds(dt, boss);
        // Environmental force on the ship — Tide vortex, Gale gust, Avatar,
        // Magnet pull/push, Singularity well (all via one hook)
        const force = boss.environmentalForce(player.x, player.y);
        if (force) {
          player.x += force.x * dt;
          player.y += force.y * dt;
        }
        world.minions.update(dt, boss.x, boss.y, player.x, player.y, bullets);
        collisions.bossPhase(boss);
      }
    }
    // Floating damage numbers from this frame's bullet impacts (docs/12 F1).
    // Exact hits show what really landed; the rest are skipped while a
    // mechanic blocks boss damage, so numbers never lie.
    for (const hit of bullets.drainHitEvents()) {
      if (hit.friendly) {
        if (boss && !boss.dead && (hit.exact || boss.isVulnerable)) {
          world.damageNumbers.spawnHit(hit.tx, hit.ty - hit.tr, hit.damage, hit.crit);
        }
      }
    }
    world.damageNumbers.update(dt);
    world.telegraphs.update(dt);
    this.d.toasts.check(run.stats);
    this.pickups(dt);

    // Lava pools burn on contact; i-frames naturally pace the ticks
    if (world.arena.touchesLava(player.x, player.y, PLAYER.radius)) {
      this.d.death.hitPlayer(GAME.enemyDamage.lava);
    }

    if (world.portal.overlaps(player.x, player.y)) {
      sfx.portal();
      this.d.shop.open();
      return;
    }

    world.camera.follow(player.x, player.y);
    world.camera.update(dt, this.d.view.width, this.d.view.height);
  }

  /** Weapon crates + item drops under the ship. */
  private pickups(dt: number): void {
    const { world } = this.d;
    const player = world.player;
    const picked = world.drops.tryPickup(player.x, player.y);
    if (picked) {
      this.d.hangar.addWeapon(picked);
      sfx.pickup();
    }
    world.items.update(dt);
    const item = world.items.tryPickup(player.x, player.y);
    if (item) this.d.rewards.collectItem(item);
  }
}
